import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile, createDefaultUserProfile } from '../utils/useUserProfile';
import '../App.css';
import './BasicFitting.css';

type AdvancedStep =
  | {
      title: string;
      question: string;
      field: 'gender' | 'handedness' | 'handicap' | 'playing_frequency' | 'height_inches' | 'wrist_to_floor_inches' | 'swing_speed' | 'swinging_type' | 'tempo' | 'course_conditions' | 'driver_model' | 'driver_distance' | 'driver_shaft' | 'driver_miss_tendency' | 'preferred_brands';
      type: 'select' | 'number' | 'text' | 'checkbox';
      options?: string[];
      placeholder?: string;
      help?: string;
      showHeightChart?: boolean;
      showWristChart?: boolean;
    }
  | {
      title: string;
      question: string;
      field: 'seven_iron' | 'seven_iron_miss_tendency' | 'driver' | 'driver_downswing_transition';
      type: 'club';
      options: {
        ballFlight?: string[];
        missTendency?: string[];
        downswingTransition?: string[];
      };
      help?: string;
    };

const ballFlightImages: Record<string, string> = {
  Low: '/BallFlightLow.jpg',
  Mid: '/BallFlightMedium.jpg',
  High: '/BallFlightHigh.jpg'
};

const missTendencyImages: Record<string, string> = {
  Hook: '/MissTendencyHook.jpg',
  Draw: '/MissTendencyDraw.jpg',
  Straight: '/MissTendencyStraight.jpg',
  Fade: '/MissTendencyFade.jpg',
  Slice: '/MissTendencySlice.jpg'
};

const transitionImages: Record<string, string> = {
  Smooth: '/TransitionSmooth.gif',
  Neutral: '/TransitionNeutral.gif',
  Abrupt: '/TransitionAbrupt.gif'
};

const AdvancedFitting: React.FC = () => {
  const navigate = useNavigate();
  const { createProfile } = useUserProfile();
  
  // Advanced profile state - includes basic info plus measurements and club data
  const [profileData, setProfileData] = useState({
    // Basic info
    gender: 'Male',
    handedness: 'Right',
    handicap: '',
    playing_frequency: '',
    preferred_brands: [] as string[],

    // Basic fitting club profile steps
    seven_iron_carry_distance: '130',
    seven_iron_ball_flight: '',
    seven_iron_miss_tendency: '',
    driver_carry_distance: '220',
    driver_loft: '10.5',
    driver_ball_flight: '',
    driver_downswing_transition: '',
    swing_speed: '90',

    // Advanced measurements
    height_inches: '',
    wrist_to_floor_inches: '',
    swinging_type: '',
    tempo: '',
    course_conditions: '',

    // Driver info
    driver_model: '',
    driver_distance: '',
    driver_shaft: '',
    driver_miss_tendency: ''
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhyThisMatters, setShowWhyThisMatters] = useState(false);

  const steps: AdvancedStep[] = [
    // Basic Info Steps
    {
      title: 'Gender',
      question: 'What is your gender?',
      field: 'gender',
      type: 'select',
      options: ['Male', 'Female']
    },
    {
      title: 'Handedness',
      question: 'Which is your dominant hand?',
      field: 'handedness',
      type: 'select',
      options: ['Right', 'Left']
    },
    {
      title: 'Handicap',
      question: 'What is your handicap range?',
      field: 'handicap',
      type: 'select',
      options: [
        'Scratch to 5 (Expert)',
        '6 to 12 (Good)',
        '13 to 20 (Average)',
        '21 to 30 (Beginner)',
        '30+ (New to Golf)',
        "I don't know"
      ]
    },
    {
      title: 'Playing Frequency',
      question: 'How often do you play golf?',
      field: 'playing_frequency',
      type: 'select',
      options: [
        'Less than once a month',
        '1-2 times per month',
        '1-2 times per week',
        '3+ times per week'
      ]
    },
    {
      title: '7-Iron',
      question: 'Tell us about your 7-Iron',
      field: 'seven_iron',
      type: 'club',
      options: {
        ballFlight: ['Low', 'Mid', 'High']
      }
    },
    {
      title: '7-Iron Miss Tendency',
      question: 'What is your 7-Iron Shot Shape?',
      field: 'seven_iron_miss_tendency',
      type: 'club',
      options: {
        missTendency: ['Hook', 'Draw', 'Fade', 'Slice']
      }
    },
    {
      title: 'Driver',
      question: 'Tell us about your driver',
      field: 'driver',
      type: 'club',
      options: {
        ballFlight: ['Low', 'Mid', 'High']
      }
    },
    {
      title: 'Driver Transition',
      question: 'What is your driver swing speed?',
      field: 'driver_downswing_transition',
      type: 'club',
      options: {
        downswingTransition: ['Smooth', 'Neutral', 'Abrupt']
      }
    },

    // Physical Measurements
    {
      title: 'Swing Type',
      question: 'How would you describe your swing type?',
      field: 'swinging_type',
      type: 'select',
      options: ['Digger', 'Neutral', 'Sweeper'],
      help: 'Digger: Takes divots, hits down on ball. Neutral: Balanced contact. Sweeper: Shallow angle, minimal divots.'
    },
    {
      title: 'Tempo',
      question: 'What is your swing tempo?',
      field: 'tempo',
      type: 'select',
      options: ['Slow', 'Moderate', 'Fast']
    },
    {
      title: 'Course Conditions',
      question: 'What course conditions do you typically play?',
      field: 'course_conditions',
      type: 'select',
      options: ['Firm', 'Soft', 'Mixed']
    },
    {
      title: 'Height',
      question: 'Tell us about your height and wrist-to-floor',
      field: 'height_inches',
      type: 'number',
      placeholder: 'e.g., 70 (5\'10")',
      showHeightChart: true,
      help: 'Stand straight and enter your height. We will estimate a wrist-to-floor value based on your height.'
    },

  ];

  const handleInputChange = (value: string) => {
    const currentStepDef = steps[currentStep] ?? steps[0];
    if (!currentStepDef) return;

    const currentField = currentStepDef.field;

    if (currentField === 'preferred_brands') {
      // Handle checkbox toggle for brands
      setProfileData(prev => {
        const currentBrands = prev.preferred_brands as string[];
        const updatedBrands = currentBrands.includes(value)
          ? currentBrands.filter(brand => brand !== value)
          : [...currentBrands, value];
        return {
          ...prev,
          preferred_brands: updatedBrands
        };
      });
    } else {
      const normalizedValue = currentField === 'driver_miss_tendency' && value === 'None' ? '' : value;
      setProfileData(prev => {
        const nextState = {
          ...prev,
          [currentField]: normalizedValue
        };

        if (currentField === 'height_inches') {
          const heightValue = Number(value);
          if (!Number.isNaN(heightValue)) {
            const wristValue = Math.round(heightValue * 0.49 * 10) / 10;
            nextState.wrist_to_floor_inches = String(wristValue);
          }
        }

        return nextState;
      });
    }
  };

  const handleSelectionNext = (nextStateOverride: Partial<typeof profileData>) => {
    const nextProfileData = { ...profileData, ...nextStateOverride };
    setProfileData(nextProfileData);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit(nextProfileData);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const convertHandicapToNumber = (handicapRange: string): number | null => {
    if (!handicapRange || handicapRange === "I don't know") return null;
    
    // Extract numbers from handicap ranges - use middle of range
    if (handicapRange.includes('Scratch to 5')) return 2;
    if (handicapRange.includes('6 to 12')) return 9;
    if (handicapRange.includes('13 to 20')) return 16;
    if (handicapRange.includes('21 to 30')) return 25;
    if (handicapRange.includes('30+')) return 35;
    
    return null;
  };

  const handleSubmit = async (finalProfileData = profileData) => {
    setIsSubmitting(true);
    try {
      // Create default profile and populate with advanced fitting data
      const defaultProfile: any = createDefaultUserProfile();
      const skillLevel = 'advanced';
      const sevenIronDistance = finalProfileData.seven_iron_carry_distance
        ? Number(finalProfileData.seven_iron_carry_distance)
        : null;
      const driverDistance = finalProfileData.driver_carry_distance
        ? Number(finalProfileData.driver_carry_distance)
        : null;
      const pitchingWedgeDistance = sevenIronDistance !== null
        ? Math.max(0, sevenIronDistance - 30)
        : null;

      defaultProfile.general = {
        ...defaultProfile.general,
        gender: finalProfileData.gender,
        handedness: finalProfileData.handedness,
        handicap: convertHandicapToNumber(finalProfileData.handicap) as any,
        playing_frequency: finalProfileData.playing_frequency,
        skill_level: skillLevel,
        preferred_brands: finalProfileData.preferred_brands.join(', '),
        height_inches: finalProfileData.height_inches ? parseInt(finalProfileData.height_inches) : null as any,
        wrist_to_floor_inches: finalProfileData.wrist_to_floor_inches ? parseInt(finalProfileData.wrist_to_floor_inches) : null as any,
        swing_speed: finalProfileData.swing_speed ? parseInt(finalProfileData.swing_speed) : null as any,
        swinging_type: finalProfileData.swinging_type || null as any,
        tempo: finalProfileData.tempo || null as any,
        course_conditions: finalProfileData.course_conditions || null as any
      };

      defaultProfile['7-Iron'] = {
        model: null,
        shaft: null,
        distance: sevenIronDistance,
        notes: null,
        miss_tendency: finalProfileData.seven_iron_miss_tendency || null,
        ball_flight: finalProfileData.seven_iron_ball_flight || null,
        loft: null
      };

      defaultProfile['Pitching Wedge'] = {
        model: null,
        shaft: null,
        distance: pitchingWedgeDistance,
        notes: null,
        miss_tendency: finalProfileData.seven_iron_miss_tendency || null,
        ball_flight: finalProfileData.seven_iron_ball_flight || null,
        loft: null
      };

      defaultProfile.Driver = {
        model: finalProfileData.driver_model || null,
        shaft: finalProfileData.driver_shaft || null,
        distance: driverDistance,
        notes: null,
        miss_tendency: finalProfileData.driver_miss_tendency || null,
        ball_flight: finalProfileData.driver_ball_flight || null,
        loft: finalProfileData.driver_loft ? Number(finalProfileData.driver_loft) : 10.5,
        downswing_transition: finalProfileData.driver_downswing_transition || null
      };

      defaultProfile.driverDistance = driverDistance;
      defaultProfile.sevenIronDistance = sevenIronDistance;
      defaultProfile.pitchingWedgeDistance = pitchingWedgeDistance;

      await createProfile(defaultProfile);

      // Redirect to club fitting page
      navigate('/club-fitting');
    } catch (error) {
      console.error('Error creating profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepData = steps[currentStep] ?? steps[0];
  const isLastStep = currentStep === steps.length - 1;

  // All fields are now optional - users can skip any step
  const canProceed = true;
  const contextualHelper =
    currentStepData?.help ||
    (currentStepData?.type === 'checkbox'
      ? 'Select all that apply. You can skip any question and still complete setup.'
      : 'Pick the option that best matches your game. You can always update this later.');
  const whyThisMatters =
    (currentStepData as any).why ||
    'We use this to personalize your fitting recommendations and suggested specs. You can update this anytime.';

  const formatHeightInches = (value: number) => {
    const totalFeet = Math.floor(value / 12);
    const remainingInches = Math.round(value % 12);
    return `${totalFeet}'${remainingInches}"`;
  };

  useEffect(() => {
    setShowWhyThisMatters(false);
  }, [currentStep]);

  if (!currentStepData) {
    return null;
  }

  return (
    <div className="nf-home fitting-container fitting-container--advanced">
      <div className="fitting-card">
        {/* Progress Bar */}
        <div className="fitting-progress">
          <div className="fitting-progress-header">
            <div className="fitting-progress-left">
              <button
                type="button"
                className="fitting-back-link"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <svg viewBox="0 0 24 24" className="fitting-back-icon" aria-hidden="true">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
                <span className="fitting-back-label">Back</span>
              </button>
            </div>
            <span className="fitting-progress-step">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <div className="fitting-progress-bar">
            <div
              className="fitting-progress-fill"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="fitting-question">
          <p className="fitting-question-eyebrow">Tell us a little about yourself:</p>
          <div className="fitting-question-header">
            <h3 className="fitting-question-title">{currentStepData.question}</h3>
          </div>

          <br />
          <div className="fitting-context-pill-wrapper">
            <button
              type="button"
              className="fitting-context-pill-button"
              onClick={() => setShowWhyThisMatters((prev) => !prev)}
              aria-expanded={showWhyThisMatters}
              aria-controls="fitting-why-this-matters"
            >
              Why this matters <span className="fitting-context-pill-icon">i</span>
            </button>
            {showWhyThisMatters && (
              <div
                id="fitting-why-this-matters"
                role="status"
                className="fitting-context-popover"
              >
                {whyThisMatters}
              </div>
            )}
          </div>
          <p className="fitting-help">{contextualHelper}</p>
          <br />

          {/* Input based on type */}
          {currentStepData.type === 'select' && Array.isArray(currentStepData.options) && (
            <div className="fitting-options">
              {currentStepData.options.map((option) => (
                <button
                  type="button"
                  key={option}
                  onClick={() => {
                    handleInputChange(option);
                    handleNext();
                  }}
                  className={`fitting-option ${
                    profileData[currentStepData.field as keyof typeof profileData] === option
                      ? 'selected'
                      : ''
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {currentStepData.type === 'club' && currentStepData.field === 'seven_iron' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1.25rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#ffffff', fontSize: '1.55rem', flex: '0 0 auto' }}>7-Iron Carry (Yards)</label>
                  <span style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.4rem' }}>{profileData.seven_iron_carry_distance || 130} yds</span>
                </div>
                <input
                  type="range"
                  min={50}
                  max={200}
                  step={1}
                  value={profileData.seven_iron_carry_distance || '130'}
                  onChange={(e) => {
                    const nextValue = Number(e.target.value);
                    const boundedValue = Number.isNaN(nextValue)
                      ? '130'
                      : String(Math.min(200, Math.max(50, nextValue)));
                    setProfileData(prev => ({ ...prev, seven_iron_carry_distance: boundedValue }));
                  }}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#ffffff', fontSize: '1.55rem' }}>Ball Flight</label>
                <div className="fitting-choice-row ball-flight-row">
                  {(currentStepData.options.ballFlight ?? []).map((option: string) => {
                    const normalizedOption = option.toLowerCase();
                    const isSelected = (profileData.seven_iron_ball_flight || '').toLowerCase() === normalizedOption;

                    return (
                      <button
                        type="button"
                        key={option}
                        className={`fitting-option fitting-choice-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectionNext({ seven_iron_ball_flight: option })}
                      >
                        <img
                          src={ballFlightImages[option]}
                          alt={`${option} ball flight`}
                          style={{ width: '100%', maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
                        />
                        <span>{option}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {currentStepData.type === 'club' && currentStepData.field === 'seven_iron_miss_tendency' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#ffffff', fontSize: '1.55rem' }}>Miss Tendency</label>
              </div>
              <div className="fitting-choice-row miss-tendency-row">
                {(currentStepData.options.missTendency ?? []).map((option: string) => {
                  const normalizedOption = option.toLowerCase();
                  const isSelected = (profileData.seven_iron_miss_tendency || '').toLowerCase() === normalizedOption;

                  return (
                    <button
                      type="button"
                      key={option}
                      className={`fitting-option fitting-choice-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectionNext({ seven_iron_miss_tendency: option })}
                    >
                      <img
                        src={missTendencyImages[option]}
                        alt={`${option} miss tendency`}
                        style={{ width: '100%', maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                      <span>{option}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {currentStepData.type === 'club' && currentStepData.field === 'driver' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1.25rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#ffffff', fontSize: '1.55rem', flex: '0 0 auto' }}>Driver Carry (Yards)</label>
                  <span style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.4rem' }}>{profileData.driver_carry_distance || 220} yds</span>
                </div>
                <input
                  type="range"
                  min={100}
                  max={350}
                  step={1}
                  value={profileData.driver_carry_distance || '220'}
                  onChange={(e) => {
                    const nextValue = Number(e.target.value);
                    const boundedValue = Number.isNaN(nextValue)
                      ? '220'
                      : String(Math.min(350, Math.max(100, nextValue)));
                    setProfileData(prev => ({ ...prev, driver_carry_distance: boundedValue }));
                  }}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#ffffff', fontSize: '1.55rem' }}>Ball Flight</label>
                <div className="fitting-choice-row ball-flight-row">
                  {(currentStepData.options.ballFlight ?? []).map((option: string) => {
                    const normalizedOption = option.toLowerCase();
                    const isSelected = (profileData.driver_ball_flight || '').toLowerCase() === normalizedOption;

                    return (
                      <button
                        type="button"
                        key={option}
                        className={`fitting-option fitting-choice-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectionNext({ driver_ball_flight: option })}
                      >
                        <img
                          src={ballFlightImages[option]}
                          alt={`${option} ball flight`}
                          style={{ width: '100%', maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
                        />
                        <span>{option}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {currentStepData.type === 'club' && currentStepData.field === 'driver_downswing_transition' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1.25rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#ffffff', fontSize: '1.55rem', flex: '0 0 auto' }}>Driver Swing Speed</label>
                  <span style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.4rem' }}>{profileData.swing_speed || '90'} mph</span>
                </div>
                <input
                  type="range"
                  min={40}
                  max={140}
                  step={1}
                  value={profileData.swing_speed || '90'}
                  onChange={(e) => {
                    const nextValue = Number(e.target.value);
                    const boundedValue = Number.isNaN(nextValue)
                      ? '90'
                      : String(Math.min(140, Math.max(40, nextValue)));
                    setProfileData(prev => ({ ...prev, swing_speed: boundedValue }));
                  }}
                  style={{ width: '100%' }}
                />
                <div style={{ marginTop: '0.25rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#ffffff', fontSize: '1.55rem' }}>Downswing Transition</label>
                </div>
              </div>

              <div className="fitting-choice-row transition-row">
                {(currentStepData.options.downswingTransition ?? []).map((option: string) => {
                  const normalizedOption = option.toLowerCase();
                  const isSelected = (profileData.driver_downswing_transition || '').toLowerCase() === normalizedOption;

                  return (
                    <button
                      type="button"
                      key={option}
                      className={`fitting-option fitting-choice-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectionNext({ driver_downswing_transition: option })}
                    >
                      <img
                        src={transitionImages[option]}
                        alt={`${option} downswing transition`}
                        style={{ width: '100%', maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                      <span>{option}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {currentStepData.type === 'checkbox' && (
            <div className="fitting-options">
              {currentStepData.options?.map((option) => (
                <label
                  key={option}
                  className={`fitting-checkbox ${
                    (profileData.preferred_brands as string[]).includes(option) ? 'selected' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={(profileData.preferred_brands as string[]).includes(option)}
                    onChange={() => handleInputChange(option)}
                    className="fitting-checkbox-input"
                  />
                  <span className="fitting-checkbox-text">{option}</span>
                </label>
              ))}
            </div>
          )}

          {currentStepData.field === 'height_inches' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <label style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.2rem' }}>Height</label>
                  <span style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.15rem' }}>
                    {formatHeightInches(Number(profileData.height_inches || 70))} ({Number(profileData.height_inches || 70)} in)
                  </span>
                </div>
                <input
                  type="range"
                  min={58}
                  max={82}
                  step={1}
                  value={profileData.height_inches || '70'}
                  onChange={(e) => {
                    const nextValue = Number(e.target.value);
                    const boundedValue = Number.isNaN(nextValue)
                      ? '70'
                      : String(Math.min(82, Math.max(58, nextValue)));
                    handleInputChange(boundedValue);
                  }}
                  style={{
                    width: '100%',
                    accentColor: '#7fe0d1',
                    filter: 'drop-shadow(0 6px 12px rgba(28,156,214,0.3))'
                  }}
                />
              </div>

              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <label style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.2rem' }}>Wrist-to-Floor</label>
                  <span style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.15rem' }}>
                    {Number(profileData.wrist_to_floor_inches || 34)} in
                  </span>
                </div>
                <input
                  type="range"
                  min={20}
                  max={50}
                  step={1}
                  value={profileData.wrist_to_floor_inches || '34'}
                  onChange={(e) => {
                    const nextValue = Number(e.target.value);
                    const boundedValue = Number.isNaN(nextValue)
                      ? '34'
                      : String(Math.min(50, Math.max(20, nextValue)));
                    setProfileData(prev => ({
                      ...prev,
                      wrist_to_floor_inches: boundedValue
                    }));
                  }}
                  style={{
                    width: '100%',
                    accentColor: '#7fe0d1',
                    filter: 'drop-shadow(0 6px 12px rgba(28,156,214,0.3))'
                  }}
                />
              </div>
            </div>
          )}

          {(currentStepData.type === 'text' || currentStepData.type === 'number') && currentStepData.field !== 'height_inches' && currentStepData.field !== 'wrist_to_floor_inches' && (
            <input
              type={currentStepData.type}
              value={profileData[currentStepData.field as keyof typeof profileData] as string}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleNext();
                }
              }}
              placeholder={(currentStepData as any).placeholder}
              className="fitting-input"
            />
          )}
        </div>

        <br />
        {/* Navigation Buttons */}
        <div className="fitting-nav">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="fitting-btn fitting-btn--ghost"
          >
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={!canProceed || isSubmitting}
            className="fitting-btn fitting-btn--primary"
          >
            {isSubmitting ? 'Creating Profile...' : isLastStep ? 'Complete' : 'Next'}
          </button>
        </div>

        {/* Skip link for all questions */}
        <div className="fitting-skip-wrapper">
          <button onClick={handleNext} className="fitting-skip">
            Skip this question
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFitting;
